import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';

const DocumentPickerModal = ({ isOpen, onClose, onConfirm, projectId, initialSelectedIds = [] }) => {
    const { user } = useAuth();
    const [documents, setDocuments] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set(initialSelectedIds));
    const [loading, setLoading] = useState(true);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [uploadCategory, setUploadCategory] = useState('deliverable');

    const categories = [
        { value: 'contract', label: 'Contracts' },
        { value: 'design', label: 'Designs' },
        { value: 'deliverable', label: 'Deliverables' },
        { value: 'other', label: 'Other' }
    ];

    useEffect(() => {
        if (isOpen && projectId) {
            fetchDocuments();
            setSelectedIds(new Set(initialSelectedIds));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, projectId]);

    const fetchDocuments = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('documents')
                .select('*')
                .eq('project_id', projectId)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setDocuments(data || []);
        } catch (error) {
            console.error('Error fetching documents:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (files) => {
        if (!files || files.length === 0) return;

        setUploadLoading(true);
        const newDocIds = [];

        try {
            for (const file of files) {
                const fileExt = file.name.split('.').pop();
                const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
                const filePath = `${projectId}/${fileName}`;

                // Upload to Storage
                const { error: uploadError } = await supabase.storage
                    .from('documents')
                    .upload(filePath, file);

                if (uploadError) throw uploadError;

                // Insert to DB
                const { data: docData, error: docError } = await supabase
                    .from('documents')
                    .insert([{
                        project_id: projectId,
                        name: file.name,
                        file_url: filePath,
                        file_size: file.size,
                        file_type: file.type || null,

                        category: uploadCategory,
                        uploaded_by: user.id
                    }])
                    .select()
                    .single();

                if (docError) throw docError;

                newDocIds.push(docData.id);
                setDocuments(prev => [docData, ...prev]);
            }

            // Auto-select uploaded files
            setSelectedIds(prev => {
                const next = new Set(prev);
                newDocIds.forEach(id => next.add(id));
                return next;
            });

        } catch (error) {
            console.error('Error uploading file:', error);
            alert('Failed to upload file(s): ' + error.message);
        } finally {
            setUploadLoading(false);
        }
    };

    const toggleSelection = (docId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(docId)) {
                next.delete(docId);
            } else {
                next.add(docId);
            }
            return next;
        });
    };

    const handleConfirm = () => {
        // Return array of selected document objects
        const selectedDocs = documents.filter(doc => selectedIds.has(doc.id));
        onConfirm(selectedDocs);
    };

    // Drag and Drop handlers
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileUpload(e.dataTransfer.files);
        }
    };

    const getFileIcon = (fileName) => {
        const ext = fileName.split('.').pop().toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(ext)) {
            return (
                <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            );
        } else if (['pdf'].includes(ext)) {
            return (
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
            );
        } // ... can add more icons
        return (
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
        );
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.95, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.95, y: 20 }}
                        className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white rounded-t-2xl z-10">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900">Select Documents</h3>
                                <p className="text-sm text-gray-500">Choose files to attach or upload new ones</p>
                            </div>
                            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">

                            {/* Category Selection for Uploads */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Category</label>
                                <select
                                    value={uploadCategory}
                                    onChange={(e) => setUploadCategory(e.target.value)}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-black focus:border-black sm:text-sm"
                                >
                                    {categories.map(cat => (
                                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Upload Drop Zone */}
                            <div
                                className={`flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-xl mb-8 transition-all duration-200 cursor-pointer ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white hover:border-gray-400'
                                    }`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                onClick={() => document.getElementById('modal-file-upload').click()}
                            >
                                <input
                                    type="file"
                                    id="modal-file-upload"
                                    multiple
                                    className="hidden"
                                    onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                                />
                                <div className="bg-gray-100 p-3 rounded-full mb-3">
                                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                </div>
                                <p className="font-semibold text-gray-900">
                                    {uploadLoading ? 'Uploading...' : 'Click or Drag files to upload'}
                                </p>
                                <p className="text-sm text-gray-500 mt-1">Files are instantly added to your selection</p>
                            </div>

                            {/* Documents Grid */}
                            {loading ? (
                                <div className="flex justify-center py-12">
                                    <div className="text-gray-500">Loading documents...</div>
                                </div>
                            ) : documents.length === 0 ? (
                                <div className="text-center py-12 text-gray-500">No documents found. Upload one above!</div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                    {documents.map(doc => {
                                        const isSelected = selectedIds.has(doc.id);
                                        return (
                                            <div
                                                key={doc.id}
                                                onClick={() => toggleSelection(doc.id)}
                                                className={`group relative flex flex-col p-4 rounded-xl border transition-all cursor-pointer ${isSelected
                                                    ? 'border-black bg-blue-50/30 ring-1 ring-black shadow-md'
                                                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                                                    }`}
                                            >
                                                {/* Checkbox */}
                                                <div className={`absolute top-3 right-3 w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'bg-black border-black' : 'border-gray-300 bg-white group-hover:border-gray-400'
                                                    }`}>
                                                    {isSelected && (
                                                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    )}
                                                </div>

                                                <div className="mb-3 p-2 bg-gray-50 rounded-lg w-fit">
                                                    {getFileIcon(doc.name)}
                                                </div>
                                                <h4 className="font-medium text-gray-900 text-sm truncate mb-1" title={doc.name}>
                                                    {doc.name}
                                                </h4>
                                                <p className="text-xs text-gray-500">
                                                    {new Date(doc.created_at).toLocaleDateString()}
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t border-gray-100 bg-white rounded-b-2xl flex justify-between items-center">
                            <span className="text-sm font-semibold text-gray-600">
                                {selectedIds.size} document{selectedIds.size !== 1 && 's'} selected
                            </span>
                            <div className="flex gap-3">
                                <button
                                    onClick={onClose}
                                    className="px-5 py-2.5 text-gray-600 font-semibold hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirm}
                                    className="px-6 py-2.5 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors shadow-lg shadow-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={uploadLoading}
                                >
                                    Attach Documents
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default DocumentPickerModal;
